import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/context/TourContext";
import { putPendingCover, removePendingCover, getAllPendingCovers } from "@/lib/pendingCovers";
import Header from "@/components/Header";
import WelcomeDialog from "@/components/WelcomeDialog";
import SagaPanel from "@/components/SagaPanel";
import AiCoverDialog from "@/components/AiCoverDialog";
import MetadataDialog from "@/components/MetadataDialog";
import WrapCoverDialog from "@/components/WrapCoverDialog";
import KdpKitDialog from "@/components/KdpKitDialog";
import AutoWriteWizard from "@/components/AutoWriteWizard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { AUDIOBOOKS_ENABLED } from "@/config/features";
import {
  Plus, BookOpen, Loader2, FileText, Sparkles, Layers, Feather, Trash2, PenLine, ArrowRight, Image as ImageIcon, Clock, Headphones, Wand2, CheckCircle2, AlertTriangle, XCircle, ShieldCheck, Download, PackageOpen, Rocket,
} from "lucide-react";

// Words -> friendly duration. Reading ~200 wpm; audiobook narration ~150 wpm.
function fmtDuration(mins) {
  if (!mins || mins < 1) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}
const readingMins = (words) => Math.max(1, Math.round((words || 0) / 200));
const listeningMins = (words) => Math.max(1, Math.round((words || 0) / 150));

function StatPill({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon className="w-3.5 h-3.5" />
      <span className="font-semibold text-foreground">{value}</span> {label}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", genre: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [descVal, setDescVal] = useState("");
  const [launchedVal, setLaunchedVal] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [aiCoverOpen, setAiCoverOpen] = useState(false);
  const [coverHealth, setCoverHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [legalName, setLegalName] = useState("");
  const [certBusy, setCertBusy] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [printTrim, setPrintTrim] = useState("6x9");
  const [printBusy, setPrintBusy] = useState(false);
  const [wrapOpen, setWrapOpen] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [autoWriteOpen, setAutoWriteOpen] = useState(false);
  const [continueSaga, setContinueSaga] = useState(null);
  const coverFileRef = React.useRef(null);

  const loadHealth = React.useCallback(async (pid) => {
    if (!pid) { setCoverHealth(null); return; }
    setHealthLoading(true);
    try {
      const { data } = await api.get(`/projects/${pid}/cover/health`);
      setCoverHealth(data);
    } catch { setCoverHealth(null); }
    finally { setHealthLoading(false); }
  }, []);

  useEffect(() => {
    if (renameTarget?.cover_path) loadHealth(renameTarget.id);
    else setCoverHealth(null);
  }, [renameTarget?.id, renameTarget?.cover_path, renameTarget?.updated_at, loadHealth]);

  const onAiCoverApplied = (patch) => {
    if (!renameTarget) return;
    const pid = renameTarget.id;
    setProjects((ps) => ps.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
    setRenameTarget((t) => (t && t.id === pid ? { ...t, ...patch } : t));
  };

  const downloadCertificate = async () => {
    if (!renameTarget) return;
    setCertBusy(true);
    try {
      const res = await api.get(`/projects/${renameTarget.id}/certificate`, { params: { legal_name: legalName.trim() }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (renameTarget.title || "certificate").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "certificate";
      a.download = `${safe}_Ownership_Certificate.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Certificate downloaded");
    } catch { toast.error("Couldn't generate the certificate"); }
    finally { setCertBusy(false); }
  };

  const downloadPrintInterior = async () => {
    if (!renameTarget) return;
    setPrintBusy(true);
    try {
      const res = await api.get(`/projects/${renameTarget.id}/print-interior`, { params: { trim: printTrim }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (renameTarget.title || "book").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "") || "book";
      a.download = `${safe}_Print_${printTrim}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Print interior downloaded");
    } catch { toast.error("Couldn't build the print interior — add some chapters first, then try again."); }
    finally { setPrintBusy(false); }
  };
  const API = process.env.REACT_APP_BACKEND_URL;
  const coverUrl = (p) =>
    p.cover_path ? `${API}/api/projects/${p.id}/cover?auth=${localStorage.getItem("cs_token")}&v=${encodeURIComponent(p.updated_at || "")}` : null;

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !renameTarget) return;
    const pid = renameTarget.id;
    setCoverBusy(true);
    // Stash the image locally first so it can't be lost if the upload fails.
    try { await putPendingCover(pid, file, file.name); } catch { /* ignore */ }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${pid}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const patch = { cover_path: data.cover_path, updated_at: data.updated_at };
      setProjects((ps) => ps.map((p) => (p.id === pid ? { ...p, ...patch } : p)));
      setRenameTarget((t) => (t && t.id === pid ? { ...t, ...patch } : t));
      await removePendingCover(pid).catch(() => {});
      toast.success("Cover updated");
    } catch (err) {
      const s = err.response?.status;
      if (!err.response || [502, 503, 504].includes(s)) {
        toast.message("Couldn't reach the server — your cover is saved on this device and will upload automatically.");
      } else {
        toast.error(err.response?.data?.detail || "Couldn't upload cover");
        await removePendingCover(pid).catch(() => {});
      }
    } finally {
      setCoverBusy(false);
      if (coverFileRef.current) coverFileRef.current.value = "";
    }
  };

  // Retry any cover uploads that were stashed locally after a failed attempt.
  const retryPendingCovers = async () => {
    let pending = [];
    try { pending = (await getAllPendingCovers()) || []; } catch { return; }
    for (const item of pending) {
      try {
        const fd = new FormData();
        fd.append("file", item.blob, item.filename || "cover");
        const { data } = await api.post(`/projects/${item.projectId}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        const patch = { cover_path: data.cover_path, updated_at: data.updated_at };
        setProjects((ps) => (ps ? ps.map((p) => (p.id === item.projectId ? { ...p, ...patch } : p)) : ps));
        await removePendingCover(item.projectId).catch(() => {});
        toast.success("Pending cover uploaded");
      } catch { /* leave it for the next attempt */ }
    }
  };
  const removeCover = async () => {
    if (!renameTarget) return;
    setCoverBusy(true);
    try {
      await api.delete(`/projects/${renameTarget.id}/cover`);
      const patch = { cover_path: null, updated_at: new Date().toISOString() };
      setProjects((ps) => ps.map((p) => (p.id === renameTarget.id ? { ...p, ...patch } : p)));
      setRenameTarget((t) => (t ? { ...t, ...patch } : t));
      toast.success("Cover removed");
    } catch { toast.error("Couldn't remove cover"); }
    finally { setCoverBusy(false); }
  };

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const healthAttempted = React.useRef(new Set());
  const load = () => api.get("/projects").then(({ data }) => setProjects(data));
  useEffect(() => { load().then(retryPendingCovers); }, []);

  // One-time backfill: measure any existing cover that hasn't been sized yet, so its card badge is accurate.
  useEffect(() => {
    if (!projects) return;
    const pending = projects.filter((p) => p.cover_path && (p.cover_health === "unknown" || p.cover_health == null) && !healthAttempted.current.has(p.id));
    if (!pending.length) return;
    let cancelled = false;
    (async () => {
      for (const p of pending) {
        healthAttempted.current.add(p.id);
        try {
          const { data } = await api.get(`/projects/${p.id}/cover/health`);
          if (cancelled || !data?.has_cover) continue;
          setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, cover_health: data.level } : x)));
        } catch { /* leave as-is */ }
      }
    })();
    return () => { cancelled = true; };
  }, [projects]);
  useEffect(() => {
    if (!user?.id) return;
    const seenKey = `codexstudio:welcomed:${user.id}`;
    try {
      const justSignedUp = sessionStorage.getItem("codexstudio:welcome") === "1";
      const alreadySeen = localStorage.getItem(seenKey) === "1";
      if (justSignedUp && !alreadySeen) {
        setWelcomeOpen(true);
        localStorage.setItem(seenKey, "1");
      }
      sessionStorage.removeItem("codexstudio:welcome");
    } catch { /* ignore */ }
  }, [user?.id]);

  const create = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/projects", form);
      setOpen(false);
      setForm({ title: "", genre: "", description: "" });
      toast.success("Project created");
      navigate(`/project/${data.id}`);
    } catch (e) {
      toast.error("Could not create project");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  const rename = async () => {
    const t = renameVal.trim();
    if (!t || !renameTarget) return;
    const { data } = await api.put(`/projects/${renameTarget.id}`, { title: t, description: descVal, launched: launchedVal });
    setProjects((ps) => ps.map((p) => (p.id === renameTarget.id ? { ...p, title: data.title, description: data.description, launched: data.launched } : p)));
    setRenameTarget(null);
    toast.success("Saved");
  };

  const displayName = user?.pen_name || user?.name;

  return (
    <div className="min-h-screen bg-background paper-texture">
      <WelcomeDialog open={welcomeOpen} onOpenChange={setWelcomeOpen} name={displayName} onStartTour={startTour} />
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 fade-up">
          <div data-tour="dashboard-desk">
            <p className="text-xs uppercase tracking-widest font-semibold text-primary mb-2">Your writing desk</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold">
              Welcome, {displayName}.
            </h1>
            <p className="text-muted-foreground mt-2">Pick up a manuscript, or start something new.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user?.ai_tools_enabled !== false && <Button data-testid="auto-write-button" size="lg" variant="outline" onClick={() => setAutoWriteOpen(true)}
              className="gap-2 font-semibold border-primary/40 text-primary hover:bg-primary/10">
              <Wand2 className="w-5 h-5" /> Auto-write a book
            </Button>}
            <NewProjectDialog {...{ open, setOpen, form, setForm, create, creating }} />
          </div>
        </div>

        {projects === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => setOpen(true)} onAutoWrite={user?.ai_tools_enabled !== false ? () => setAutoWriteOpen(true) : null} />
        ) : (
          <>
            <SagaPanel projects={projects} navigate={navigate} onChanged={load}
              onContinueSaga={user?.ai_tools_enabled !== false ? (s) => { setContinueSaga({ id: s.id, title: s.title }); setAutoWriteOpen(true); } : undefined} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((p, i) => (
              <div key={p.id} data-testid={`project-card-${p.id}`}
                className="group relative rounded-2xl border border-border bg-card hover:border-primary/50 transition-all duration-300 overflow-hidden fade-up hover:shadow-lg hover:shadow-primary/5"
                style={{ animationDelay: `${i * 40}ms` }}>
                <button onClick={() => navigate(`/project/${p.id}`)} className="w-full text-left">
                  {coverUrl(p) ? (
                    <div className="h-36 w-full overflow-hidden bg-secondary relative">
                      <img src={coverUrl(p)} alt={`${p.title} cover`} data-testid={`cover-img-${p.id}`}
                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                      <CoverBadge level={p.cover_health} pid={p.id} />
                    </div>
                  ) : (
                    <div className="h-36 w-full flex items-center justify-center relative overflow-hidden"
                      style={{ background: `linear-gradient(135deg, hsl(${(p.title.charCodeAt(0) * 7) % 360} 45% 30%), hsl(${(p.title.charCodeAt(0) * 7 + 40) % 360} 45% 18%))` }}>
                      <span className="font-display text-4xl font-bold text-white/85">{(p.title[0] || "?").toUpperCase()}</span>
                      <CoverBadge level="none" pid={p.id} />
                    </div>
                  )}
                  <div className="p-6 pb-4">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/12 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex items-center gap-1.5">
                    {p.launched ? (
                      <span data-testid={`launched-badge-${p.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                        <Rocket className="w-3 h-3" /> Launched
                      </span>
                    ) : null}
                    {p.series_order ? (
                      <span data-testid={`saga-badge-${p.id}`} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-primary/15 text-primary">
                        Book {p.series_order}
                      </span>
                    ) : null}
                    {p.genre && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-secondary text-secondary-foreground">
                        {p.genre}
                      </span>
                    )}
                    </div>
                  </div>
                  <h3 className="font-display text-2xl font-bold leading-tight mb-1.5 group-hover:text-primary transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
                    {p.description || "No synopsis yet."}
                  </p>
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border">
                    <StatPill icon={Layers} value={p.chapter_count} label="ch." />
                    <StatPill icon={Sparkles} value={p.entity_count} label="details" />
                    <StatPill icon={FileText} value={p.word_count?.toLocaleString()} label="words" />
                  </div>
                  {p.word_count > 0 && (
                    <div className="flex items-center gap-4 mt-2.5 text-xs text-muted-foreground" data-testid={`project-times-${p.id}`}>
                      <span className="flex items-center gap-1.5" title="Estimated reading time (~200 words/min)">
                        <Clock className="w-3.5 h-3.5" /> ~{fmtDuration(readingMins(p.word_count))} read
                      </span>
                      {AUDIOBOOKS_ENABLED && (
                        <span className="flex items-center gap-1.5" title="Estimated audiobook listening time (~150 words/min)">
                          <Headphones className="w-3.5 h-3.5" /> ~{fmtDuration(listeningMins(p.word_count))} listen
                        </span>
                      )}
                    </div>
                  )}
                  </div>
                </button>
                <div className="flex items-center justify-between px-6 py-3 bg-secondary/40 border-t border-border">
                  <span className="text-xs text-primary font-semibold flex items-center gap-1 group-hover:gap-2 transition-all cursor-pointer" onClick={() => navigate(`/project/${p.id}`)}>
                    Open studio <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex items-center gap-2.5">
                    <button data-testid={`rename-project-${p.id}`} title="Edit book"
                      onClick={() => { setRenameTarget(p); setRenameVal(p.title); setDescVal(p.description || ""); setLaunchedVal(!!p.launched); setLegalName(user?.name || ""); }}
                      className="text-muted-foreground hover:text-primary transition-colors">
                      <PenLine className="w-4 h-4" />
                    </button>
                    <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button data-testid={`delete-project-${p.id}`} className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-popover">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{p.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently removes the project, all chapters and its codex. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p.id)} data-testid={`confirm-delete-${p.id}`}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </main>

      <Dialog open={!!renameTarget} onOpenChange={(v) => !v && setRenameTarget(null)}>
        <DialogContent className="bg-popover" data-testid="rename-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <PenLine className="w-5 h-5 text-primary" /> Edit book
            </DialogTitle>
            <DialogDescription>Update the title or synopsis. This won't affect anything you've written.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div className="space-y-1.5">
              <Label>Cover</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-24 rounded-lg overflow-hidden border border-border shrink-0 bg-secondary flex items-center justify-center">
                  {renameTarget && coverUrl(renameTarget) ? (
                    <img src={coverUrl(renameTarget)} alt="cover preview" data-testid="cover-preview" className="w-full h-full object-cover" />
                  ) : (
                    <BookOpen className="w-6 h-6 text-muted-foreground/50" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={coverFileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={uploadCover} data-testid="cover-file-input" />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={coverBusy} data-testid="upload-cover-button"
                      onClick={() => coverFileRef.current?.click()} className="gap-2">
                      {coverBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Upload image
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={coverBusy} data-testid="generate-cover-button"
                      onClick={() => setAiCoverOpen(true)} className="gap-2">
                      <Wand2 className="w-4 h-4 text-primary" /> Generate with AI
                    </Button>
                  </div>
                  {renameTarget?.cover_path && (
                    <Button type="button" variant="ghost" size="sm" disabled={coverBusy} data-testid="remove-cover-button"
                      onClick={removeCover} className="gap-2 text-muted-foreground hover:text-destructive justify-start">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">JPG, PNG, WEBP or GIF · up to 8MB</p>
              {renameTarget?.cover_path && <CoverHealth health={coverHealth} loading={healthLoading} />}
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} autoFocus
                placeholder="The Obsidian Cipher" data-testid="rename-project-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Synopsis</Label>
              <Textarea value={descVal} onChange={(e) => setDescVal(e.target.value)} rows={4}
                placeholder="A one or two line snapshot of what your story is about…" data-testid="rename-project-synopsis" />
            </div>
            <label className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5 cursor-pointer">
              <span className="flex items-center gap-2 text-sm">
                <Rocket className="w-4 h-4 text-primary" /> Launched on KDP
                <span className="text-[11px] text-muted-foreground font-normal">Mark this book as published</span>
              </span>
              <Switch checked={launchedVal} onCheckedChange={setLaunchedVal} data-testid="rename-project-launched" />
            </label>
            <div className="space-y-1.5 pt-3 border-t border-border">
              <Label className="flex items-center gap-1.5 text-primary"><PackageOpen className="w-4 h-4" /> Prepare for KDP</Label>
              <p className="text-[11px] text-muted-foreground">Get one ZIP with everything Amazon KDP needs — eBook, print interior, covers, listing metadata, certificate and a step-by-step guide.</p>
              <Button type="button" size="sm" onClick={() => setKitOpen(true)}
                data-testid="open-kdp-kit-button" className="gap-2">
                <Download className="w-4 h-4" /> Download KDP kit
              </Button>
              <p className="text-[11px] text-muted-foreground pt-1">Or grab individual pieces below.</p>
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border">
              <Label className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> Ownership certificate</Label>
              <p className="text-[11px] text-muted-foreground">A one-page PDF stating you own 100% of this work, with an AI-assistance disclosure — handy for KDP and your records.</p>
              <Input value={legalName} onChange={(e) => setLegalName(e.target.value)}
                placeholder="Your legal name (for the certificate)" data-testid="cert-legal-name" />
              <Button type="button" variant="outline" size="sm" onClick={downloadCertificate} disabled={certBusy}
                data-testid="download-certificate-button" className="gap-2">
                {certBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download certificate
              </Button>
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border">
              <Label className="flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> KDP metadata pack</Label>
              <p className="text-[11px] text-muted-foreground">AI-written blurb, 7 keywords and category picks — paste-ready, with a one-tap link to create the title on KDP.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => setMetadataOpen(true)}
                data-testid="open-metadata-button" className="gap-2">
                <Sparkles className="w-4 h-4 text-primary" /> Blurb, keywords &amp; categories
              </Button>
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border">
              <Label className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-primary" /> Paperback interior (print PDF)</Label>
              <p className="text-[11px] text-muted-foreground">A KDP print-ready interior — trim size, mirrored gutter margins, running headers, page numbers and a contents page. Chapters start on the right-hand page.</p>
              <div className="flex flex-wrap items-center gap-2">
                <select value={printTrim} onChange={(e) => setPrintTrim(e.target.value)} data-testid="print-trim-select"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                  <option value="5x8">5 × 8 in</option>
                  <option value="5.25x8">5.25 × 8 in</option>
                  <option value="5.5x8.5">5.5 × 8.5 in</option>
                  <option value="6x9">6 × 9 in</option>
                </select>
                <Button type="button" variant="outline" size="sm" onClick={downloadPrintInterior} disabled={printBusy}
                  data-testid="download-print-interior-button" className="gap-2">
                  {printBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download interior
                </Button>
              </div>
            </div>
            <div className="space-y-1.5 pt-2 border-t border-border">
              <Label className="flex items-center gap-1.5"><ImageIcon className="w-4 h-4 text-primary" /> Wraparound cover (print PDF)</Label>
              <p className="text-[11px] text-muted-foreground">Back + spine + front as one KDP-ready PDF, with the spine width auto-set from your page count.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => setWrapOpen(true)}
                data-testid="open-wrap-cover-button" className="gap-2">
                <ImageIcon className="w-4 h-4 text-primary" /> Build wraparound cover
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={rename} disabled={!renameVal.trim()} data-testid="rename-project-submit" className="gap-2">
              <PenLine className="w-4 h-4" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {renameTarget && (
        <AiCoverDialog open={aiCoverOpen} onOpenChange={setAiCoverOpen} project={renameTarget} onApplied={onAiCoverApplied} />
      )}
      {renameTarget && (
        <MetadataDialog open={metadataOpen} onOpenChange={setMetadataOpen} project={renameTarget} />
      )}
      {renameTarget && (
        <WrapCoverDialog open={wrapOpen} onOpenChange={setWrapOpen} project={renameTarget} />
      )}
      {renameTarget && (
        <KdpKitDialog open={kitOpen} onOpenChange={setKitOpen} project={renameTarget} defaultLegalName={legalName} />
      )}
      <AutoWriteWizard open={autoWriteOpen} onOpenChange={(v) => { setAutoWriteOpen(v); if (!v) setContinueSaga(null); }}
        authorName={user?.pen_name || user?.name || ""}
        initialSaga={continueSaga}
        onDone={(pid) => { if (pid) navigate(`/project/${pid}`); else load(); }} />
    </div>
  );
}

function CoverBadge({ level, pid }) {
  const map = {
    ready: { cls: "bg-emerald-500/90 text-white", icon: CheckCircle2, label: "KDP-ready" },
    warn: { cls: "bg-amber-500/90 text-white", icon: AlertTriangle, label: "Check cover" },
    fail: { cls: "bg-destructive/90 text-white", icon: XCircle, label: "Low-res" },
    none: { cls: "bg-black/55 text-white backdrop-blur-sm", icon: ImageIcon, label: "No cover" },
  }[level];
  if (!map) return null; // unknown / not yet measured -> no badge
  const Icon = map.icon;
  return (
    <span data-testid={`cover-badge-${pid}`}
      className={`absolute top-2 right-2 z-10 flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm ${map.cls}`}>
      <Icon className="w-3 h-3" /> {map.label}
    </span>
  );
}

function CoverHealth({ health, loading }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1" data-testid="cover-health-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking KDP readiness…
      </div>
    );
  }
  if (!health || !health.has_cover) return null;
  const meta = {
    ready: { icon: CheckCircle2, cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/30", label: "KDP-ready" },
    warn: { icon: AlertTriangle, cls: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/30", label: "Usable — could be better" },
    fail: { icon: XCircle, cls: "text-destructive", bg: "bg-destructive/10 border-destructive/30", label: "Not print-ready" },
  }[health.level] || {};
  const Icon = meta.icon || CheckCircle2;
  const mIcon = { pass: CheckCircle2, warn: AlertTriangle, fail: XCircle };
  const mCls = { pass: "text-emerald-600 dark:text-emerald-400", warn: "text-amber-600 dark:text-amber-400", fail: "text-destructive" };
  return (
    <div className={`mt-2 rounded-lg border px-3 py-2.5 ${meta.bg}`} data-testid="cover-health">
      <div className={`flex items-center gap-2 text-sm font-semibold ${meta.cls}`}>
        <Icon className="w-4 h-4" /> {meta.label}
        <span className="ml-auto text-[11px] font-normal text-muted-foreground">{health.width}×{health.height}px · {health.ratio}:1</span>
      </div>
      <ul className="mt-1.5 space-y-1">
        {(health.messages || []).map((m, i) => {
          const MI = mIcon[m.type] || CheckCircle2;
          return (
            <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <MI className={`w-3 h-3 mt-0.5 shrink-0 ${mCls[m.type] || ""}`} /> <span>{m.text}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function NewProjectDialog({ open, setOpen, form, setForm, create, creating }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-project-button" size="lg" className="gap-2 font-semibold shrink-0">
          <Plus className="w-5 h-5" /> New project
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-popover">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Feather className="w-5 h-5 text-primary" /> Start a new story
          </DialogTitle>
          <DialogDescription>Every great novel starts with a single line.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="The Obsidian Cipher" data-testid="project-title-input" autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label>Genre <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })}
              placeholder="Sci-fi thriller" data-testid="project-genre-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Synopsis <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="A short pitch for your story..." rows={3} data-testid="project-desc-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={creating || !form.title.trim()} data-testid="create-project-submit" className="gap-2">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />} Create & write
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ onCreate, onAutoWrite }) {
  return (
    <div className="text-center py-24 fade-up">
      <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
        <Feather className="w-10 h-10 text-primary" />
      </div>
      <h2 className="font-display text-3xl font-bold mb-2">Your desk is empty</h2>
      <p className="text-muted-foreground max-w-md mx-auto mb-6">
        Start writing yourself, let AI write a whole book from a single idea, or import an existing manuscript.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {onAutoWrite && <Button onClick={onAutoWrite} size="lg" variant="outline" data-testid="empty-auto-write-button"
          className="gap-2 border-primary/40 text-primary hover:bg-primary/10">
          <Wand2 className="w-5 h-5" /> Auto-write a book
        </Button>}
        <Button onClick={onCreate} size="lg" data-testid="empty-create-button" className="gap-2">
          <Plus className="w-5 h-5" /> Create your first project
        </Button>
      </div>
    </div>
  );
}
